import React, { useState } from 'react';
import { 
  Book, 
  UserCheck, 
  Users, 
  BarChart2, 
  Calendar, 
  AlertTriangle, 
  PieChart, 
  BookOpen, 
  MessageSquare, 
  CheckCircle, 
  ChevronRight, 
  Settings, 
  Bell, 
  Eye, 
  ArrowLeft,
  Zap,
  FileText,
  Upload,
  Search,
  Filter,
  Clock
} from 'lucide-react';
import styled from 'styled-components';
import TeacherHeader from '../TeacherHeader';
import TeacherFooter from '../TeacherFooter';
import Dashboard from './Dashboard';
import ClassDetails from './ClassDetails';
import StudentDetails from './StudentDetails';

export const JourneyContainer = styled.div`
  min-height: 100vh;
  background-color: #f9f9fb;
  display: flex;
  flex-direction: column;
`;

export const JourneyMain = styled.main`
  flex: 1;
  max-width: 48rem;
  margin: 0 auto;
  width: 100%;
`;

export const JourneyContent = styled.div`
  padding: 1rem;
`;

export const BackButton = styled.button`
  color: #7c3aed;
  display: flex;
  align-items: center;
  margin-right: 0.5rem;
`;

export const PageTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
`;

export const PageHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

interface Class {
  id: number;
  name: string;
  subject: string;
  students: number;
  alerts: number;
}

interface Student {
  id: number;
  name: string;
  grade: string;
  progress: number;
  alert: boolean;
  lastActivity: string;
}

interface Notification {
  id: number;
  type: 'alert' | 'info' | 'success';
  message: string;
  time: string;
}

// Mock data
const mockClasses: Class[] = [
  { id: 1, name: 'Grade 7A', subject: 'Science', students: 28, alerts: 3 },
  { id: 2, name: 'Grade 7B', subject: 'Science', students: 30, alerts: 0 },
  { id: 3, name: 'Grade 6A', subject: 'Science', students: 26, alerts: 1 }
];

const mockNotifications: Notification[] = [
  { id: 1, type: 'alert', message: '3 students struggling with "The Heart" topic', time: '10m ago' },
  { id: 2, type: 'info', message: 'Class 7B assignments need grading', time: '1h ago' },
  { id: 3, type: 'success', message: 'Class performance report ready', time: '2h ago' }
];

const mockStudents: Student[] = [
  { id: 1, name: 'Mary Johnson', grade: 'Grade 7A', progress: 78, alert: true, lastActivity: '2h ago' },
  { id: 2, name: 'James Smith', grade: 'Grade 7A', progress: 92, alert: false, lastActivity: '1h ago' },
  { id: 3, name: 'Sarah Williams', grade: 'Grade 7A', progress: 65, alert: true, lastActivity: '30m ago' },
  { id: 4, name: 'David Brown', grade: 'Grade 7A', progress: 82, alert: false, lastActivity: '4h ago' },
  { id: 5, name: 'Emma Davis', grade: 'Grade 7A', progress: 59, alert: true, lastActivity: '1d ago' }
];

const TeacherJourney: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [selectedSubject, setSelectedSubject] = useState('Science');
  const [selectedTerm, setSelectedTerm] = useState('Term 2');
  
  const handleSelectClass = (classItem: Class) => {
    setSelectedClass(classItem);
    setCurrentStep(2);
  };
  
  const handleSelectStudent = (student: Student) => {
    setSelectedStudent(student);
    setCurrentStep(3);
  };
  
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      if (currentStep === 3) {
        setSelectedStudent(null);
      } else if (currentStep === 2) {
        setSelectedClass(null);
      }
    }
  };

  return (
    <JourneyContainer>
      <TeacherHeader />
      
      <JourneyMain>
        <JourneyContent>
          {currentStep === 1 && (
            <Dashboard 
              classes={mockClasses}
              notifications={mockNotifications}
              selectedTerm={selectedTerm}
              onSelectClass={handleSelectClass}
            />
          )}
          
          {currentStep === 2 && selectedClass && (
            <ClassDetails 
              classData={selectedClass}
              students={mockStudents}
              subject={selectedSubject}
              term={selectedTerm}
              onBack={handleBack}
              onSelectStudent={handleSelectStudent}
            />
          )}
          
          {currentStep === 3 && selectedStudent && (
            <StudentDetails 
              studentData={selectedStudent}
              onBack={handleBack}
            />
          )}
        </JourneyContent>
      </JourneyMain>

      <TeacherFooter />
    </JourneyContainer>
  );
};

export default TeacherJourney; 